import homeAsaImage from "../../assets/service_images/drillingprojects1.jpg";
import {
  ShieldCheck,
  BriefcaseBusiness,
  Layers,
  Users,
  Target,
} from "lucide-react";
import LazyImage from "../common/LazyImage/LazyImage";
import MotionReveal from "../common/MotionReveal/MotionReveal";
import MotionStagger from "../common/Motion/MotionStagger";
import MotionItem from "../common/Motion/MotionItem";

function WhyChooseSAC() {
  const features = [
    {
      icon: <BriefcaseBusiness size={22} />,
      title: "Operational Expertise",
      desc: "Built by professionals with direct oilfield and drilling operations experience.",
    },
    {
      icon: <Users size={22} />,
      title: "Workforce Excellence",
      desc: "Technically competent personnel prepared for real-world operational environments.",
    },
    {
      icon: <Layers size={22} />,
      title: "Integrated Solutions",
      desc: "Drilling, workover, maintenance, engineering and project operations — one trusted partner.",
    },
    {
      icon: <ShieldCheck size={22} />,
      title: "Safety & Compliance",
      desc: "Industry-leading HSE standards and operational excellence across every project.",
    },
  ];

  return (
    <section className="relative bg-white overflow-x-hidden py-16 lg:py-20">
      <div className="relative max-w-7xl mx-auto px-6">
        {/* Editorial header */}
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-start">
          <MotionReveal variant="fadeUp" className="lg:col-span-7">
            <span className="uppercase tracking-[0.4em] text-red-600 text-[11px] sm:text-xs font-semibold">
              About South Asia Consultancy
            </span>

            <h2 className="mt-5 text-3xl sm:text-4xl lg:text-[2.75rem] xl:text-5xl font-bold text-slate-900 leading-[1.12] tracking-tight">
              Integrated Energy Services
              <span className="block text-red-500 mt-2 lg:mt-3">
                Built on Operational Excellence
              </span>
            </h2>

            <div className="mt-10 h-px w-16 bg-red-600" aria-hidden="true" />
          </MotionReveal>

          <MotionReveal variant="fadeUp" className="lg:col-span-5 lg:pt-14">
            <p className="text-base sm:text-lg text-slate-600 leading-relaxed text-justify">
              South Asia Consultancy (SAC) is a leading integrated energy services
              company delivering Operations &amp; Maintenance, Drilling Support,
              Workover Services, Technical Workforce Solutions, Crew Management,
              Engineering Support, and Oilfield Project Services across offshore
              and onshore energy operations.
            </p>
          </MotionReveal>
        </div>

        {/* Image-led editorial block */}
        <MotionReveal variant="fadeUp" className="relative mt-16 lg:mt-24">
          <div className="relative lg:grid lg:grid-cols-12 lg:gap-0">
            <div className="hidden lg:block lg:col-span-1" aria-hidden="true" />

            <div className="lg:col-span-11 lg:col-start-2 relative">
              <div className="relative aspect-[4/3] sm:aspect-[16/10] lg:aspect-[16/9] lg:min-h-[480px] xl:min-h-[540px] overflow-hidden">
                <LazyImage
                  src={homeAsaImage}
                  alt="Offshore oil and gas platforms representing SAC operational capability"
                  className="w-full h-full object-cover object-center"
                />
                <div
                  className="absolute inset-0 bg-gradient-to-r from-white via-white/20 to-transparent lg:from-white/90 lg:via-transparent pointer-events-none"
                  aria-hidden="true"
                />
              </div>

              <div className="absolute top-6 left-6 sm:top-8 sm:left-8 lg:top-auto lg:bottom-10 lg:left-0 lg:-translate-x-1/4 z-10">
                <div className="bg-red-600 text-white px-7 py-5 sm:px-9 sm:py-6">
                  <p className="text-[10px] sm:text-xs font-semibold uppercase tracking-[0.35em] text-red-100">
                    Established
                  </p>
                  <p className="text-4xl sm:text-5xl font-bold mt-1 tracking-tight leading-none">
                    2014
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-10 lg:mt-0 lg:absolute lg:bottom-12 lg:left-0 lg:w-[44%] xl:w-[40%] z-10">
            <p className="text-base sm:text-lg text-slate-600 leading-relaxed text-justify bg-white lg:pr-10">
              With a strong operational presence across India and growing
              international capabilities spanning the Middle East, Southeast Asia,
              Africa, and CIS regions, SAC has established itself as a trusted
              partner to national oil companies, drilling contractors, energy
              operators, and oilfield service organizations.
            </p>

            <p className="mt-6 text-base sm:text-lg text-slate-600 leading-relaxed text-justify bg-white lg:pr-10">
              Our success is built upon a simple philosophy — combine highly
              competent people, proven operational systems, and an uncompromising
              commitment to safety to deliver measurable value for our clients.
            </p>
          </div>
        </MotionReveal>

        {/* Remaining editorial body */}
        <div className="mt-16 lg:mt-28 grid lg:grid-cols-12 gap-10 lg:gap-16">
          <MotionReveal variant="fadeRight" className="lg:col-span-5 lg:col-start-1">
            <p className="text-base sm:text-lg text-slate-600 leading-relaxed text-justify">
              From supporting drilling rigs operating in some of the world&apos;s
              most demanding environments to providing highly skilled technical
              personnel and integrated maintenance solutions, SAC continues to play
              a critical role in supporting the energy industry&apos;s operational
              success.
            </p>
          </MotionReveal>

          <MotionReveal variant="fadeLeft" className="lg:col-span-6 lg:col-start-7 space-y-6">
            <p className="text-base sm:text-lg text-slate-600 leading-relaxed text-justify">
              Today, our teams support drilling, workover, production support,
              maintenance, engineering, logistics, and project execution activities
              across multiple geographies while maintaining the highest standards
              of safety, quality, environmental responsibility, and operational
              excellence.
            </p>

            <p className="text-base sm:text-lg text-slate-600 leading-relaxed text-justify">
              Whether supporting a single asset or managing large-scale
              multi-location operations, SAC remains committed to helping clients
              improve productivity, optimize performance, reduce operational
              risk, and achieve sustainable growth.
            </p>
          </MotionReveal>
        </div>

        <div className="mt-20 lg:mt-28 h-px w-full bg-slate-200" aria-hidden="true" />

        {/* Purpose */}
        <MotionReveal variant="fadeUp" className="mt-16 lg:mt-20 max-w-4xl">
          <div className="flex gap-5 sm:gap-6">
            <div className="shrink-0 w-px bg-red-600 self-stretch" aria-hidden="true" />

            <div>
              <div className="flex items-center gap-3">
                <Target size={18} className="text-red-600 shrink-0" aria-hidden="true" />
                <p className="text-[11px] font-bold uppercase tracking-[0.3em] text-red-600">
                  Our Purpose
                </p>
              </div>

              <p className="mt-5 text-base sm:text-lg text-slate-600 leading-relaxed text-justify">
                To support the global energy industry through reliable operational
                solutions, highly skilled professionals, and innovative service
                delivery models that enhance safety, efficiency, and long-term
                asset performance.
              </p>
            </div>
          </div>
        </MotionReveal>

        {/* Feature blocks */}
        <MotionStagger className="mt-20 lg:mt-24 border-t border-slate-200">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4">
            {features.map((feature, index) => (
              <MotionItem
                key={feature.title}
                variant="fadeUp"
                className={`
                  group relative px-6 py-8 sm:py-10 border-b border-slate-200
                  ${index % 2 === 0 ? "sm:border-r" : ""}
                  ${index < 2 ? "sm:border-b" : ""}
                  lg:border-b-0
                  ${index < features.length - 1 ? "lg:border-r" : ""}
                `}
              >
                <span
                  className="text-[10px] font-semibold tracking-[0.35em] text-slate-400 uppercase"
                  aria-hidden="true"
                >
                  {String(index + 1).padStart(2, "0")}
                </span>

                <div className="mt-4 text-red-600">{feature.icon}</div>

                <h3 className="mt-4 font-bold text-sm sm:text-base text-slate-900">
                  {feature.title}
                </h3>

                <p className="mt-3 text-xs sm:text-sm text-slate-600 text-justify leading-relaxed">
                  {feature.desc}
                </p>
              </MotionItem>
            ))}
          </div>
        </MotionStagger>
      </div>
    </section>
  );
}

export default WhyChooseSAC;
